import {
  Button,
  ButtonColor,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Column,
  Container,
  ContainerSize,
  Heading,
  Layout,
  LayoutType,
  Navbar,
  NavbarBrand,
  NavbarCollapse,
  NavbarToggler,
  Nav,
  NavLink,
  Row,
  Text,
  Wrapper,
} from '@rebootui';
import CodeSnippet from "../../components/controls/CodeSnippet";
import CodePreview from "../../components/controls/CodePreview";
import CodeHighlighter from "../../components/controls/CodeHighlighter";

function StarterTemplatePreview() {
  return (
    <Layout type={LayoutType.Sidebar}>
      <Layout.Header>
        <Navbar className="navbar-expand-lg">
          <Container size={ContainerSize.Fluid} className='px-4'>
            <NavbarBrand><text className='fw-bold'>RebootUI</text></NavbarBrand>
            <NavbarToggler />
            <NavbarCollapse>
              <Nav className="ms-auto">
                <NavLink href="#">Home</NavLink>
                <NavLink href="#">About</NavLink>
                <NavLink href="#">Contact</NavLink>
              </Nav>
            </NavbarCollapse>
          </Container>
        </Navbar>
      </Layout.Header>
      <Layout.Sidebar>
        <Wrapper className="p-2">
          <Nav className="flex-column gap-2">
            <NavLink href="#">Home</NavLink>
            <NavLink href="#">About</NavLink>
            <NavLink href="#">Contact</NavLink>
          </Nav>
        </Wrapper>
      </Layout.Sidebar>
      <Layout.Content>
        <Container className="py-5">
          <Row>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 1</CardTitle>
                  <CardText>
                    Build responsive layouts with our Grid system.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 2</CardTitle>
                  <CardText>
                    Rich component library with TypeScript support.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 3</CardTitle>
                  <CardText>
                    Fully customizable with SCSS variables.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
          </Row>
        </Container>
      </Layout.Content>
    </Layout>
  );
}

type GetStartedPageProps = {}

const GetStartedPage = ({ }: GetStartedPageProps) => {
  return (
    <Container size={ContainerSize.Fluid}>
      <Heading el="h1" className="fw-semibold mb-3 ls-1">
        Get Started
      </Heading>
      <Text className="mb-5 fs-5">
        Get started with RebootUI, a modern React component library built on Bootstrap 5.
        Build beautiful, responsive web applications quickly and easily.
      </Text>

      {/* Introduction */}
      <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
        Introduction
      </Heading>
      <Text className="mb-4">
        RebootUI is a comprehensive React component library that combines the power of Bootstrap 5
        with modern React best practices. It provides a rich set of customizable, accessible, and
        responsive components to help you build stunning user interfaces faster.
      </Text>
      <Text className="mb-4">
        <strong>Key Features:</strong>
      </Text>
      <ul className="mb-4">
        <li><strong>Modern React Components</strong> - Built with TypeScript and React 18+</li>
        <li><strong>Bootstrap 5 Foundation</strong> - Leverages Bootstrap's proven design system</li>
        <li><strong>Type-Safe</strong> - Full TypeScript support with comprehensive type definitions</li>
        <li><strong>Customizable</strong> - Easily customize styles with SCSS variables</li>
        <li><strong>Accessible</strong> - Built with accessibility in mind</li>
        <li><strong>Tree-Shakeable</strong> - Import only what you need for optimal bundle size</li>
      </ul>

      {/* Installation */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Installation
      </Heading>
      <Text className="mb-4">
        Install RebootUI using npm or yarn:
      </Text>

      <CodeSnippet>
        <CodeHighlighter language="bash">
          {installCode}
        </CodeHighlighter>
      </CodeSnippet>

      <Text className="mb-4 mt-4">
        RebootUI has peer dependencies on React 18+ and React DOM. Make sure you have them installed:
      </Text>

      <CodeSnippet>
        <CodeHighlighter language="bash">
          {peerDependenciesCode}
        </CodeHighlighter>
      </CodeSnippet>

      {/* CSS Import */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Import CSS
      </Heading>
      <Text className="mb-4">
        After installation, you need to import the CSS. RebootUI provides three CSS options:
      </Text>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Option 1: Complete RebootUI (Recommended)
      </Heading>
      <Text className="mb-3">
        Includes Bootstrap base styles plus RebootUI custom overrides:
      </Text>
      <CodeSnippet>
        <CodeHighlighter language="javascript">
          {cssCompleteCode}
        </CodeHighlighter>
      </CodeSnippet>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Option 2: Only RebootUI Overrides
      </Heading>
      <Text className="mb-3">
        If you already have Bootstrap in your project, import just the RebootUI overrides:
      </Text>
      <CodeSnippet>
        <CodeHighlighter language="javascript">
          {cssOverridesCode}
        </CodeHighlighter>
      </CodeSnippet>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Option 3: Mix and Match
      </Heading>
      <Text className="mb-3">
        Import Bootstrap and RebootUI styles separately for maximum flexibility:
      </Text>
      <CodeSnippet>
        <CodeHighlighter language="javascript">
          {cssMixMatchCode}
        </CodeHighlighter>
      </CodeSnippet>

      {/* Quick Start */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Quick Start Template
      </Heading>
      <Text className="mb-4">
        Here's a minimal example to get you started. Create a new React app and replace your
        <Text el="code" className="bg-light px-2 py-1 rounded mx-1">App.tsx</Text>
        with the following:
      </Text>

      <CodeSnippet>
        <CodeHighlighter language="tsx">
          {quickStartCode}
        </CodeHighlighter>
      </CodeSnippet>

      {/* Starter Template */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Starter Template
      </Heading>
      <Text className="mb-4">
        Here's a more complete starter template using the <Text el="strong">Layout</Text> shell
        (header, sidebar, and main content), a top navigation bar, and sample components:
      </Text>

      <CodeSnippet>
        <CodePreview>
          <StarterTemplatePreview />
        </CodePreview>
        <CodeHighlighter language="tsx">
          {starterTemplateCode}
        </CodeHighlighter>
      </CodeSnippet>

      {/* Usage Examples */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Component Usage
      </Heading>
      <Text className="mb-4">
        RebootUI components are designed to be intuitive and easy to use. Here are some basic examples:
      </Text>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Buttons
      </Heading>
      <CodeSnippet>
        <CodeHighlighter language="tsx">
          {buttonExampleCode}
        </CodeHighlighter>
      </CodeSnippet>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Alerts
      </Heading>
      <CodeSnippet>
        <CodeHighlighter language="tsx">
          {alertExampleCode}
        </CodeHighlighter>
      </CodeSnippet>

      <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
        Cards
      </Heading>
      <CodeSnippet>
        <CodeHighlighter language="tsx">
          {cardExampleCode}
        </CodeHighlighter>
      </CodeSnippet>

      {/* Next Steps */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Next Steps
      </Heading>
      <Text className="mb-4">
        Now that you have RebootUI installed, explore our comprehensive component library:
      </Text>
      <ul className="mb-5">
        <li><strong>Layout Components</strong> - Container, Grid, Stack for building responsive layouts</li>
        <li><strong>UI Components</strong> - Buttons, Cards, Modals, Alerts, and more</li>
        <li><strong>Form Components</strong> - Input fields, checkboxes, selects (coming soon)</li>
        <li><strong>Navigation</strong> - Navbar, Nav, Breadcrumb, Pagination</li>
      </ul>

      <div className="alert alert-info mb-5">
        <strong>💡 Tip:</strong> Check out the individual component pages in the sidebar
        to see detailed examples, props documentation, and usage patterns.
      </div>

      {/* Support */}
      <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
        Need Help?
      </Heading>
      <div className="mb-4">
        <ul className="mb-0">
          <li>📦 <strong>NPM Package:</strong> <a href="https://www.npmjs.com/package/@reactufy/rebootui" target="_blank" rel="noopener noreferrer">@reactufy/rebootui</a></li>
          <li>📖 <strong>Documentation:</strong> Browse the components in the sidebar</li>
          <li>🐛 <strong>Issues:</strong> Report bugs or request features on GitHub</li>
        </ul>
      </div>
    </Container>
  )
}

export default GetStartedPage;

// Code snippets
const installCode = `# Using npm
npm install @reactufy/rebootui

# Using yarn
yarn add @reactufy/rebootui`;

const peerDependenciesCode = `# Install peer dependencies if not already installed
npm install react@^18.0.0 react-dom@^18.0.0

# Or with yarn
yarn add react@^18.0.0 react-dom@^18.0.0`;

const cssCompleteCode = `// Import in your main entry file (e.g., main.tsx or App.tsx)
import '@reactufy/rebootui/dist/rebootui-complete.css';`;

const cssOverridesCode = `// Import Bootstrap separately and add RebootUI overrides
import 'bootstrap/dist/css/bootstrap.min.css';
import '@reactufy/rebootui/dist/rebootui-overrides.css';`;

const cssMixMatchCode = `// Import Bootstrap and RebootUI styles separately
import '@reactufy/rebootui/dist/bootstrap.css';        // Standard Bootstrap
import '@reactufy/rebootui/dist/rebootui-overrides.css'; // RebootUI enhancements
import { Button, Alert, Modal } from '@reactufy/rebootui';`;

const quickStartCode = `import React from 'react';
import '@reactufy/rebootui/dist/rebootui-complete.css';
import { Container, Button, ButtonColor, Heading, Alert, AlertColor } from '@reactufy/rebootui';

function App() {
  return (
    <Container className="py-5">
      <Heading className="mb-4">Welcome to RebootUI!</Heading>
      
      <Alert color={AlertColor.Success} className="mb-4">
        🎉 You've successfully set up RebootUI!
      </Alert>
      
      <Button color={ButtonColor.Primary}>
        Get Started
      </Button>
    </Container>
  );
}

export default App;`;

const starterTemplateCode = `import '@reactufy/rebootui/dist/rebootui-complete.css';
import {
  Button,
  ButtonColor,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Column,
  Container,
  Layout,
  LayoutType,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavbarCollapse,
  Nav,
  NavLink,
  Row,
  Wrapper,
} from '@reactufy/rebootui';

function App() {
  return (
    <Layout type={LayoutType.Sidebar}>
      <Layout.Header>
        <Navbar className="navbar-expand-lg">
          <Container size={ContainerSize.Fluid} className='px-4'>
            <NavbarBrand>RebootUI App</NavbarBrand>
            <NavbarToggler />
            <NavbarCollapse>
              <Nav className="ms-auto">
                <NavLink href="#">Home</NavLink>
                <NavLink href="#">About</NavLink>
                <NavLink href="#">Contact</NavLink>
              </Nav>
            </NavbarCollapse>
          </Container>
        </Navbar>
      </Layout.Header>
      <Layout.Sidebar>
        <Wrapper className="p-4">
          <Nav className="flex-column gap-2">
            <NavLink href="#">Home</NavLink>
            <NavLink href="#">About</NavLink>
            <NavLink href="#">Contact</NavLink>
          </Nav>
        </Wrapper>
      </Layout.Sidebar>
      <Layout.Content>
        <Container className="py-5">
          <Row>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 1</CardTitle>
                  <CardText>
                    Build responsive layouts with our Grid system.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 2</CardTitle>
                  <CardText>
                    Rich component library with TypeScript support.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
            <Column col={Col.ColMd4}>
              <Card className="mb-4">
                <CardBody>
                  <CardTitle>Feature 3</CardTitle>
                  <CardText>
                    Fully customizable with SCSS variables.
                  </CardText>
                  <Button color={ButtonColor.Primary}>Learn More</Button>
                </CardBody>
              </Card>
            </Column>
          </Row>
        </Container>
      </Layout.Content>
    </Layout>
  );
}

export default App;`;

const buttonExampleCode = `import { Button, ButtonColor } from '@reactufy/rebootui';

<Button color={ButtonColor.Primary}>Primary</Button>
<Button color={ButtonColor.Success}>Success</Button>
<Button color={ButtonColor.Danger}>Danger</Button>`;

const alertExampleCode = `import { Alert, AlertColor } from '@reactufy/rebootui';

<Alert color={AlertColor.Info}>
  This is an info alert with some information!
</Alert>`;

const cardExampleCode = `import { Card, CardBody, CardTitle, CardText } from '@reactufy/rebootui';

<Card>
  <CardBody>
    <CardTitle>Card Title</CardTitle>
    <CardText>
      This is some card content with text.
    </CardText>
  </CardBody>
</Card>`;
